import React from 'react';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MobileDialog from '@shared/ui/MobileDialog/MobileDialog';

import { BaseButton, Input } from '../../../BaseComponents';
import { DropdownRendererParameters } from '../../../Autocomplete/components/Autocomplete';
import LocationOptionsList from '../LocationOptionsList/LocationOptionsList';
import Loader from '../../../Loader';

import { useCityOptions } from '../../../LocationAutocomplete/hooks';

import { Location } from '../../types';
import { LocationType } from '../../../types';

import { useTheme } from '../../../theme';
import { disableAutocompleteProps } from '../../../utils';
import { useDebounce } from '../../../hooks';

type Props = Partial<DropdownRendererParameters<Location>> & {
	type: LocationType;
	isLoading?: boolean;
};

const MobileDropdown: React.FunctionComponent<Props> = ({
	isOpen,
	options,
	onClose,
	onInputChange,
	type,
	onAdditionalButtonClick,
	isLoading,
	...listProps
}) => {
	const { MobileDropdown: css } = useTheme('LocationAutocomplete');
	const { t } = useTranslation('LocationAutocomplete');

	const [query, setQuery] = useState('');

	const debouncedQuery = useDebounce(query, 500);

	const { dropdownOptions, showAdditionalButton } = useCityOptions(options);

	const onChange = (event: ChangeEvent<HTMLInputElement>): void => {
		setQuery(event.target.value);
	};

	React.useEffect(() => {
		if (onInputChange) {
			const event = {
				target: { value: debouncedQuery },
				currentTarget: { value: debouncedQuery }
			} as ChangeEvent<HTMLInputElement>;
			onInputChange(event);
		}
	}, [debouncedQuery, onInputChange]);

	return (
		<MobileDialog
		classes={{
			content: css.mobileDropdown__content
		}}
			title={`${t(type === LocationType.Departure ? 'From' : 'To')}`}
			isOpen={isOpen}
			onClose={onClose}
			footer={
				showAdditionalButton && onAdditionalButtonClick ? (
					<div className={css.mobileDropdown__showAllDirectionsWrapper}>
						<BaseButton
							variant="smallThird"
							className={css.mobileDropdown__showAllDirections}
							onMouseDown={onAdditionalButtonClick}
						>
							{t('show all directions')}
						</BaseButton>
					</div>
				) : null
			}
		>
			<>
				<Input
					name="mobileDialogInput"
					className={css.mobileDropdown__input}
					type="text"
					placeholder={`${t(type === LocationType.Departure ? 'From' : 'To')}`}
					value={query}
					onChange={onChange}
					autoFocus={true}
					spellCheck={false}
					autoCorrect="off"
					inputProps={disableAutocompleteProps}
				/>
				{isLoading ? (
					<Loader className={css.mobileDropdown__loader} hideText />
				) : (
					<LocationOptionsList options={dropdownOptions} showButton={false} {...listProps} />
				)}
			</>
		</MobileDialog>
	);
};

export default MobileDropdown;
